import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { authApi, setAuthToken } from "./api";

const TOKEN_KEY = "customer_token";
const norm = (u) => ({ ...u, addresses: u.addresses || [] });

const Ctx = createContext(null);

// Optional customer account. Phase 2B: existing-account login + full account management
// (register / edit profile / add-edit-delete address / marketing) via the existing production API.
export function AuthProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState(null);
  const qc = useQueryClient();

  useEffect(() => {
    const tok = localStorage.getItem(TOKEN_KEY);
    if (!tok) { setReady(true); return; }
    setAuthToken(tok);
    authApi
      .me()
      .then((u) => setUser(norm(u)))
      .catch(() => { setAuthToken(null); localStorage.removeItem(TOKEN_KEY); })
      .finally(() => setReady(true));
  }, []);

  const session = useCallback((r) => {
    setAuthToken(r.access_token);
    localStorage.setItem(TOKEN_KEY, r.access_token);
    setUser(norm(r.user));
    qc.invalidateQueries({ queryKey: ["orders"] });
  }, [qc]);

  const login = useCallback(async (phone, password) => session(await authApi.login({ phone, password })), [session]);
  const register = useCallback(async (body) => session(await authApi.register(body)), [session]);

  const logout = useCallback(() => {
    setAuthToken(null);
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    qc.removeQueries({ queryKey: ["orders", "account"] });
  }, [qc]);

  const updateProfile = useCallback(async (body) => setUser(norm(await authApi.updateProfile(body))), []);
  const setMarketing = useCallback(async (consent) => setUser(norm(await authApi.setMarketing(consent))), []);
  const addAddress = useCallback(async (body) => setUser(norm(await authApi.addAddress(body))), []);
  const updateAddress = useCallback(async (id, body) => setUser(norm(await authApi.updateAddress(id, body))), []);
  const deleteAddress = useCallback(async (id) => setUser(norm(await authApi.deleteAddress(id))), []);

  const value = useMemo(
    () => ({ ready, user, login, register, logout, updateProfile, setMarketing, addAddress, updateAddress, deleteAddress }),
    [ready, user, login, register, logout, updateProfile, setMarketing, addAddress, updateAddress, deleteAddress]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error("AuthProvider missing");
  return c;
}
